import { useMemo, useRef, useEffect } from "react";

import { Link, useLocation, useParams } from "react-router-dom";

import Rellax from "rellax";

import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import DefaultFooter from "examples/Footers/DefaultFooter";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import SimpleBlogCard from "examples/Cards/BlogCards/SimpleBlogCard";

import footerRoutes from "footer.routes";
import routes from "routes";
import FaqAccordionList from "studio-edifique/components/FaqAccordionList";
import SectionHeading from "studio-edifique/components/SectionHeading";
import SeoHead from "studio-edifique/components/SeoHead";
import NotFoundPage from "studio-edifique/pages/NotFoundPage";
import {
  blogPosts,
  company,
  createArticleSchema,
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
  servicePages,
  getMobileBackgroundImage,
} from "studio-edifique/siteContent";

function BlogArticlePage() {
  const headerRef = useRef(null);
  const { slug } = useParams();
  const { pathname } = useLocation();

  const post = useMemo(() => blogPosts.find((item) => item.slug === slug), [slug]);
  const relatedPosts = useMemo(
    () => blogPosts.filter((item) => item.slug !== slug).slice(0, 3),
    [slug]
  );
  const relatedServices = useMemo(
    () => servicePages.filter((service) => service.articleSlugs.includes(slug)),
    [slug]
  );

  useEffect(() => {
    if (!post || !headerRef.current) {
      return undefined;
    }

    const parallax = new Rellax(headerRef.current, {
      speed: -3,
    });

    return () => parallax.destroy();
  }, [post]);

  if (!post) {
    return <NotFoundPage />;
  }

  return (
    <>
      <SeoHead
        title={post.title}
        description={post.seoDescription}
        keywords={post.keywords}
        path={pathname}
        image={post.heroImage}
        responsiveBackground
        type="article"
        schema={[
          createOrganizationSchema(),
          createArticleSchema(post),
          createBreadcrumbSchema([
            { name: "Início", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: pathname },
          ]),
          createFaqSchema(post.faqs),
        ]}
      />
      <MKBox bgColor="white" py={0.25}>
        <DefaultNavbar
          brand={company.brandName}
          routes={routes}
          action={{
            type: "external",
            route: company.whatsappUrl,
            label: "Solicitar atendimento",
            color: "info",
          }}
          transparent
          relative
        />
      </MKBox>
      <MKBox
        ref={headerRef}
        minHeight="76vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba } }) =>
            `${linearGradient(rgba("#1C1A1A", 0.9), rgba("#111111", 0.58))}, url(${
              post.heroImage
            })`,
          backgroundSize: "cover",
          "@media (max-width: 600px)": {
            backgroundImage: ({ functions: { linearGradient, rgba } }) =>
              `${linearGradient(
                rgba("#1C1A1A", 0.9),
                rgba("#111111", 0.58)
              )}, url(${getMobileBackgroundImage(post.heroImage)})`,
          },
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid container item xs={12} lg={8} justifyContent="center" flexDirection="column">
            <MKTypography variant="button" color="info" textTransform="uppercase" letterSpacing={2}>
              {post.category} • {post.readingTime}
            </MKTypography>
            <MKTypography
              variant="h1"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                mt: 1,
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              {post.title}
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.84} mt={2} mb={3} pr={{ lg: 6 }}>
              {post.excerpt}
            </MKTypography>
            <MKBox display="flex" flexWrap="wrap" gap={1.5}>
              <MKButton component={Link} to="/blog" variant="outlined" color="white">
                Voltar ao blog
              </MKButton>
              <MKButton
                component="a"
                href={company.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                variant="gradient"
                color="info"
              >
                Tirar dúvida com a equipe
              </MKButton>
            </MKBox>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={8}>
          <Container>
            <Grid container spacing={4}>
              {post.highlights.map((item) => (
                <Grid item xs={12} md={4} key={item}>
                  <Card
                    sx={{
                      p: 3,
                      height: "100%",
                      border: ({ borders: { borderWidth }, palette: { grey } }) =>
                        `${borderWidth[1]} solid ${grey[200]}`,
                    }}
                  >
                    <MKTypography variant="button" textTransform="uppercase" color="info">
                      Ponto-chave
                    </MKTypography>
                    <MKTypography variant="body2" mt={1.5} color="text">
                      {item}
                    </MKTypography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        {post.sections.map((section) => (
          <MKBox component="section" py={4} key={section.heading}>
            <Container>
              <Grid container justifyContent="center">
                <Grid item xs={12} lg={9}>
                  <MKTypography variant="h3" mb={2}>
                    {section.heading}
                  </MKTypography>
                  {section.paragraphs.map((paragraph) => (
                    <MKTypography key={paragraph} variant="body1" color="text" mb={2.5}>
                      {paragraph}
                    </MKTypography>
                  ))}
                </Grid>
              </Grid>
            </Container>
          </MKBox>
        ))}

        {relatedServices.length > 0 && (
          <MKBox component="section" py={5}>
            <Container>
              <MKTypography variant="h3" component="h2" mb={2}>
                Serviços relacionados a este conteúdo
              </MKTypography>
              <MKTypography variant="body1" color="text" mb={3}>
                Conheça como a equipe organiza esse trabalho no projeto e no acompanhamento técnico.
              </MKTypography>
              <MKBox display="flex" flexWrap="wrap" gap={2}>
                {relatedServices.map((service) => (
                  <MKButton
                    key={service.slug}
                    component={Link}
                    to={`/servicos/${service.slug}`}
                    variant="outlined"
                    color="info"
                  >
                    {service.name}
                  </MKButton>
                ))}
              </MKBox>
            </Container>
          </MKBox>
        )}

        <MKBox
          component="section"
          py={8}
          sx={{
            background: ({ palette: { grey } }) => grey[100],
            mx: -2,
            px: { xs: 2, lg: 0 },
          }}
        >
          <Container>
            <Grid container spacing={6}>
              <Grid item xs={12} lg={5}>
                <SectionHeading
                  eyebrow="Perguntas frequentes"
                  title="Dúvidas que costumam aparecer sobre esse tema"
                  description="Respostas rápidas para reforçar o entendimento técnico do artigo."
                />
              </Grid>
              <Grid item xs={12} lg={7}>
                <FaqAccordionList items={post.faqs} />
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        <MKBox component="section" py={8}>
          <Container>
            <SectionHeading
              eyebrow="Leitura relacionada"
              title="Outros conteúdos da Studio Edifique"
              description="Se este tema fez sentido para o seu momento de obra, vale continuar a leitura por aqui."
            />
            <Grid container spacing={4}>
              {relatedPosts.map((relatedPost) => (
                <Grid item xs={12} md={4} key={relatedPost.slug}>
                  <SimpleBlogCard
                    image={relatedPost.coverImage}
                    title={relatedPost.title}
                    description={relatedPost.excerpt}
                    action={{
                      type: "internal",
                      route: `/blog/${relatedPost.slug}`,
                      color: "dark",
                      label: "Ler artigo",
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        <MKBox
          component="section"
          py={8}
          mx={-2}
          px={{ xs: 2, lg: 0 }}
          sx={{
            backgroundImage: ({ functions: { linearGradient, rgba } }) =>
              `${linearGradient(rgba("#8C1A15", 0.92), rgba("#4B120F", 0.92))}, url(${
                post.heroImage
              })`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={8}>
                <SectionHeading
                  eyebrow="Aplicar na prática"
                  title="Se esse assunto impacta sua obra, vale discutir o caso com critério técnico."
                  description="A equipe da Studio Edifique pode orientar próximos passos em projeto, obra ou análise técnica."
                  light
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <MKBox display="flex" flexDirection="column" gap={1.5}>
                  <MKButton
                    component="a"
                    href={company.whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    variant="contained"
                    color="white"
                  >
                    Falar no WhatsApp
                  </MKButton>
                  <MKButton component={Link} to="/contato" variant="outlined" color="white">
                    Ir para contato
                  </MKButton>
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default BlogArticlePage;
